import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { PageHeader, Btn, Input, Select, Textarea, Modal, Card, Badge, EmptyState, fmtDate } from "@/components/ui-kit";
import { Plus, MapPin, CheckCircle2 } from "lucide-react";

const VISIT_PURPOSES = [
  { value: "new_customer", label: "New Customer (Acquisition)" },
  { value: "follow_up", label: "Follow-up" },
  { value: "payment_collection", label: "Payment Collection" },
  { value: "order_taking", label: "Order Taking" },
  { value: "product_detailing", label: "Product Detailing / Presentation" },
  { value: "sampling", label: "Sampling" },
  { value: "complaint", label: "Complaint Handling" },
  { value: "market_survey", label: "Market Survey" },
  { value: "stock_check", label: "Stock Check" },
  { value: "other", label: "Other" },
];

const PURPOSE_LABEL = Object.fromEntries(VISIT_PURPOSES.map((p) => [p.value, p.label]));

export default function Visits() {
  const { t } = useLang();
  const [visits, setVisits] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [pharmacies, setPharmacies] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ visit_type: "pharmacy", target_id: "", visit_purpose: "follow_up", notes: "", outcome: "success" });
  const [busy, setBusy] = useState(false);
  const [gpsErr, setGpsErr] = useState("");

  const load = async () => {
    const [v, d, p] = await Promise.all([api.get("/visits"), api.get("/doctors"), api.get("/pharmacies")]);
    setVisits(v.data); setDoctors(d.data); setPharmacies(p.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setGpsErr("");
    try {
      const targetList = form.visit_type === "doctor" ? doctors : pharmacies;
      const target = targetList.find(t => t.id === form.target_id);
      const payload = {
        ...form,
        target_name: target?.full_name || target?.name || "",
        check_in_time: new Date().toISOString(),
      };

      // Try GPS
      if (navigator.geolocation) {
        await new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            (pos) => {
              payload.check_in_lat = pos.coords.latitude;
              payload.check_in_lng = pos.coords.longitude;
              resolve();
            },
            () => { setGpsErr("GPS not available"); resolve(); },
            { timeout: 5000 }
          );
        });
      }
      await api.post("/visits", payload);
      setOpen(false);
      setForm({ visit_type: "pharmacy", target_id: "", visit_purpose: "follow_up", notes: "", outcome: "success" });
      await load();
    } catch (e) { alert("Error: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const checkout = async (id) => {
    const out = { check_out_time: new Date().toISOString() };
    if (navigator.geolocation) {
      await new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
          (pos) => { out.check_out_lat = pos.coords.latitude; out.check_out_lng = pos.coords.longitude; resolve(); },
          () => resolve(),
          { timeout: 5000 }
        );
      });
    }
    await api.put(`/visits/${id}/checkout`, out);
    await load();
  };

  return (
    <div data-testid="visits-page">
      <PageHeader
        title={t("visits")}
        subtitle="Field force route execution & GPS tracking"
        actions={<Btn onClick={() => setOpen(true)} data-testid="add-visit-btn"><Plus className="w-4 h-4"/>{t("checkIn")}</Btn>}
      />

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-y border-slate-200 text-xs font-bold uppercase tracking-wider text-slate-500">
                <th className="py-2 px-3 text-start">Type</th>
                <th className="py-2 px-3 text-start">Target</th>
                <th className="py-2 px-3 text-start">Purpose</th>
                <th className="py-2 px-3 text-start">{t("rep")}</th>
                <th className="py-2 px-3 text-start">Check In</th>
                <th className="py-2 px-3 text-start">Check Out</th>
                <th className="py-2 px-3 text-start">GPS</th>
                <th className="py-2 px-3 text-start">Outcome</th>
                <th className="py-2 px-3 text-start">{t("notes")}</th>
                <th className="py-2 px-3 text-end">{t("actions")}</th>
              </tr>
            </thead>
            <tbody>
              {visits.map((v) => (
                <tr key={v.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-2 px-3"><Badge color={v.visit_type === "doctor" ? "purple" : "blue"}>{v.visit_type}</Badge></td>
                  <td className="py-2 px-3 font-semibold text-slate-900">{v.target_name}</td>
                  <td className="py-2 px-3 text-xs"><Badge color="slate">{PURPOSE_LABEL[v.visit_purpose] || v.visit_purpose || "-"}</Badge></td>
                  <td className="py-2 px-3 text-slate-700">{v.rep_name}</td>
                  <td className="py-2 px-3 text-xs">{fmtDate(v.check_in_time || v.created_at)}<div className="text-[10px] text-slate-500">{v.check_in_time && new Date(v.check_in_time).toLocaleTimeString()}</div></td>
                  <td className="py-2 px-3 text-xs">{v.check_out_time ? new Date(v.check_out_time).toLocaleTimeString() : <Badge color="amber">In Progress</Badge>}</td>
                  <td className="py-2 px-3">
                    {v.check_in_lat ? (
                      <a target="_blank" rel="noopener noreferrer" href={`https://www.openstreetmap.org/?mlat=${v.check_in_lat}&mlon=${v.check_in_lng}#map=16/${v.check_in_lat}/${v.check_in_lng}`} className="text-blue-700 inline-flex items-center gap-1 text-xs"><MapPin className="w-3 h-3"/>Map</a>
                    ) : <span className="text-slate-400 text-xs">-</span>}
                  </td>
                  <td className="py-2 px-3"><Badge color={v.outcome === "success" ? "green" : v.outcome === "failed" ? "red" : "amber"}>{v.outcome || "-"}</Badge></td>
                  <td className="py-2 px-3 text-xs text-slate-600 max-w-xs truncate">{v.notes || "-"}</td>
                  <td className="py-2 px-3 text-end">
                    {!v.check_out_time && (
                      <button onClick={() => checkout(v.id)} className="text-xs font-semibold text-emerald-700 hover:bg-emerald-50 px-2 py-1 rounded inline-flex items-center gap-1" data-testid={`checkout-${v.id}`}>
                        <CheckCircle2 className="w-3 h-3"/>{t("checkOut")}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {visits.length === 0 && <EmptyState message={t("noData")}/>}
        </div>
      </Card>

      <Modal open={open} onClose={() => setOpen(false)} title={t("checkIn")}>
        <form onSubmit={submit} className="space-y-3" data-testid="visit-form">
          <Select label="Visit Type *" value={form.visit_type} onChange={(e) => setForm({...form, visit_type: e.target.value, target_id: ""})}>
            <option value="pharmacy">Pharmacy</option><option value="doctor">Doctor</option>
          </Select>
          <Select label="Target *" required value={form.target_id} onChange={(e) => setForm({...form, target_id: e.target.value})} data-testid="visit-target">
            <option value="">--Select--</option>
            {(form.visit_type === "doctor" ? doctors : pharmacies).map(o => (
              <option key={o.id} value={o.id}>{o.full_name || o.name} {o.city ? `· ${o.city}` : ""}</option>
            ))}
          </Select>
          <Select label="Visit Purpose *" required value={form.visit_purpose} onChange={(e) => setForm({...form, visit_purpose: e.target.value})} data-testid="visit-purpose">
            {VISIT_PURPOSES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </Select>
          <Textarea label="Notes" rows={3} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})}/>
          <Input label="Samples Given" value={form.samples_given || ""} onChange={(e) => setForm({...form, samples_given: e.target.value})}/>
          <Input label="Competitor Observed" value={form.competitor_observed || ""} onChange={(e) => setForm({...form, competitor_observed: e.target.value})}/>
          <Input label="Follow-up Date" type="date" value={form.follow_up_date || ""} onChange={(e) => setForm({...form, follow_up_date: e.target.value})}/>
          <Input label="Follow-up Task" value={form.follow_up_task || ""} onChange={(e) => setForm({...form, follow_up_task: e.target.value})}/>
          <Select label="Outcome" value={form.outcome} onChange={(e) => setForm({...form, outcome: e.target.value})}>
            <option value="success">Success</option><option value="pending">Pending</option><option value="failed">Failed</option>
          </Select>
          {gpsErr && <div className="text-xs text-amber-700 bg-amber-50 p-2 rounded">{gpsErr}</div>}
          <div className="text-xs text-slate-500">📍 GPS will be captured automatically</div>
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
            <Btn variant="ghost" type="button" onClick={() => setOpen(false)}>{t("cancel")}</Btn>
            <Btn type="submit" disabled={busy} data-testid="visit-submit">{busy ? t("loading") : t("checkIn")}</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
